import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import { EnlaceVolver } from "@/components/EnlaceVolver";
import {
  InformeFormulario,
  type InformeFormularioHandle,
  type InformeValues,
} from "@/components/informes/InformeFormulario";
import { rolVacio } from "@/components/informes/FilaDeRol";
import { definicionDesdeInforme } from "@/components/informes/definicionDeDocumentos";
import { superficieEnHectareas } from "@/components/informes/superficies";
import {
  PestanaDeDocumentos,
  type PestanaDeDocumentosHandle,
} from "@/components/informes/PestanaDeDocumentos";
import {
  PestanaDeDescripcion,
  type PestanaDeDescripcionHandle,
} from "@/components/informes/PestanaDeDescripcion";
import {
  PestanaDeMaquinarias,
  type PestanaDeMaquinariasHandle,
} from "@/components/informes/PestanaDeMaquinarias";
import {
  PestanaDeInstalaciones,
  type PestanaDeInstalacionesHandle,
} from "@/components/informes/PestanaDeInstalaciones";
import {
  PestanaDeConstrucciones,
  type PestanaDeConstruccionesHandle,
} from "@/components/informes/PestanaDeConstrucciones";
import {
  PestanaDePlantaciones,
  type PestanaDePlantacionesHandle,
} from "@/components/informes/PestanaDePlantaciones";
import {
  PestanaDeSuelos,
  type PestanaDeSuelosHandle,
} from "@/components/informes/PestanaDeSuelos";
import {
  PestanaDeAguas,
  type PestanaDeAguasHandle,
} from "@/components/informes/PestanaDeAguas";
import { BarraDePestanas, EncabezadoDePestana } from "@/components/informes/BarraDePestanas";
import { estadoDeLaPestana } from "@/components/informes/estadoDeLaPestana";
import { BannerDeCotizacion } from "@/components/informes/BannerDeCotizacion";
import { BotonDeGeneracion } from "@/components/generacion/BotonDeGeneracion";
import { BotonGuiaDeTerreno } from "@/components/informes/GuiaDeTerreno";
import { BotonDeNotasGenerales } from "@/components/informes/NotasGeneralesDeTerreno";
import { irALasNotasGenerales } from "@/components/informes/notasGenerales";
import { FacturacionDelCliente } from "@/components/informes/FacturacionDelCliente";
import { estadoDelTerreno } from "@/components/informes/terrenoDeAntecedentes";
import { PestanaDeReferencias } from "@/components/informes/PestanaDeReferencias";
import { BarraDeSincronizaciones } from "@/components/informes/BarraDeSincronizaciones";
import { ProveedorDeDescarteDeTerreno } from "@/components/informes/ProveedorDeDescarteDeTerreno";
import { PestanaDeTerreno } from "@/components/informes/PestanaDeTerreno";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useCuadroDelInforme } from "@/hooks/useCuadroDelInforme";
import { useGuardiaDeCambios } from "@/hooks/useGuardiaDeCambios";
import {
  leerOrigen,
  ORIGEN_DE_LA_EDICION,
  raizDelOrigen,
  retornoA,
  type Origen,
} from "@/lib/origenDeNavegacion";
import {
  useActualizarInforme,
  useEnviarATerreno,
  useAdjuntarArchivoDeAntecedente,
  useCatalogosDelInforme,
  useAnotarElArchivo,
  useCorregirFechaDelArchivo,
  useGuardarDescripcion,
  useGuardarAguas,
  useGuardarSuelos,
  useGuardarDocumentos,
  useInforme,
  useQuitarArchivoDeAntecedente,
  useQuitarFotografia,
  useQuitarImagenDelExpediente,
  useQuitarImagenDelRol,
  useClasificarArchivoDeAntecedente,
  useReprocesarArchivoDeAntecedente,
  useSubirFotografia,
  useSubirImagenDelExpediente,
  useSubirImagenDelRol,
} from "@/hooks/useInformes";
import { useReferenciasDelInforme } from "@/hooks/useReferencias";
import { useRegistroDeTerreno } from "@/hooks/useRegistroDeTerreno";
import { ProveedorDeResaltado } from "@/hooks/useResaltadoDeSincronizaciones";
import type { SincronizacionDeTerreno } from "@/lib/terreno";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { subirEnSecuencia } from "@/lib/subirEnSecuencia";
import { formatearMomento } from "@/lib/fechas";
import {
  esCambioDePestanaDelMismoInforme,
  esPestanaInicial,
  admiteTerreno,
  esSeccionDelExpediente,
  ETIQUETA_ESTADO,
  VARIANTE_ESTADO_INFORME,
  PESTANA_POR_DEFECTO,
  PESTANAS,
  pestanaDesdeLaUrl,
  pestanasDisponibles,
  rutaDePestana,
  ROLES_RESPONSABLE,
  urlDeArchivoDeAntecedente,
  type CatalogosDelInforme,
  type Informe,
  type PestanaDelInforme,
  type PestanaDeMercado,
  type TipoDeImagenDelInforme,
} from "@/lib/informes";
import type { ClaseDeEvidencia } from "@/lib/referencias";

/**
 * Las dos pestañas de mercado: qué clase de evidencia muestra cada una.
 *
 * EL RÓTULO ES CORTO y no el `ETIQUETA_CLASE` del catálogo ("Transacciones CBR",
 * "Ofertas de mercado"), por la misma razón que existe `ETIQUETA_CORTA_ESTADO`:
 * acá comparten barra con tres rótulos largos —"Antecedentes generales",
 * "legales", "históricos"— y los equipos de Consultek rinden como un viewport de
 * 800 px de ancho, donde esas tres ya ocupan casi todo el espacio útil. En el
 * catálogo el rótulo largo sí entra: ahí las pestañas son solo dos.
 */
const PESTANAS_DE_MERCADO: {
  pestana: PestanaDeMercado;
  clase: ClaseDeEvidencia;
  etiqueta: string;
}[] = [
  { pestana: "cbr", clase: "transaccion", etiqueta: "CBR" },
  { pestana: "ofertas", clase: "oferta", etiqueta: "Ofertas" },
];

/**
 * Traduce la ficha del API a los valores del formulario.
 *
 * Los números llegan como string por el cast `decimal:2` del backend y los
 * nulos se vuelven `""`: un `<input value={null}>` pasa a no-controlado y React
 * avisa por consola, además de perder el valor al primer tipeo.
 */
function valoresDesdeInforme(informe: Informe): InformeValues {
  return {
    numero_informe: informe.numero_informe ?? "",
    cliente_nombre: informe.cliente_nombre ?? "",
    cargo_cliente: informe.cargo_cliente ?? "",
    propiedad_nombre: informe.propiedad_nombre ?? "",
    telefono_contacto: informe.telefono_contacto ?? "",
    email_contacto: informe.email_contacto ?? "",
    solicitante_nombre: informe.solicitante_nombre ?? "",
    // Las dos juntas o ninguna: media coordenada guardada sería un dato roto,
    // y el `decimal:6` llega como string.
    coordenada:
      informe.latitud !== null && informe.longitud !== null
        ? { latitud: Number(informe.latitud), longitud: Number(informe.longitud) }
        : null,
    direccion: informe.direccion ?? "",
    descripcion_ubicacion: informe.descripcion_ubicacion ?? "",
    descripcion: informe.descripcion ?? "",
    // Un informe anterior a los formatos no tiene ninguno: queda vacío y la
    // persona tiene que elegirlo para poder guardar.
    formato_salida_id: informe.formato_salida ? String(informe.formato_salida.id) : "",
    // Los TRES estados de RF-E3, de vuelta al select: `null` es "Sin
    // verificar", que no es lo mismo que "No".
    es_retasacion: informe.es_retasacion === null ? "" : informe.es_retasacion ? "si" : "no",
    unidad_superficie: informe.unidad_superficie ?? "ha",
    nota_imagen: informe.nota_imagen ?? "",
    /* LOS ROLES LLEVAN SU `id`, y eso REVIERTE el "se reemplazan enteros" que
       hubo hasta que existió la pestaña "Descripción de la propiedad". Aquello
       se podía porque el rol no tenía identidad propia para el usuario ni nada
       colgando —a diferencia del bloque extinto, dueño de un objeto en el
       bucket—. Hoy tiene las dos cosas: la captura de su límite y la prosa de
       sus deslindes. Sin el id, guardar esta pestaña se llevaría el trabajo de
       aquella sin que nada fallara. */
    roles: informe.roles?.length
      ? informe.roles.map((rol) => ({
          id: rol.id,
          rol_sii: rol.rol_sii,
          // El código vuelve al selector para que reabrir la pestaña muestre
          // la comuna ya elegida; el nombre viaja al lado porque es lo que se
          // muestra en los informes anteriores al maestro, que no tienen
          // código.
          comuna_codigo: rol.comuna_codigo ?? "",
          comuna: rol.comuna ?? "",
          superficie: rol.superficie ?? "",
          superficie_construida: rol.superficie_construida ?? "",
        }))
      : [rolVacio()],
    /* Un informe ANTERIOR a las plantillas no tiene plantilla ni ítems: arranca
       sin elegir, y el formulario pide una para poder guardar. */
    plantilla_id: informe.plantilla ? String(informe.plantilla.id) : "",
    items: (informe.items ?? []).map((i) => i.maestro_item_id),
    descripcion_ubicacion_validada: informe.descripcion_ubicacion_validada,
    descripcion_validada: informe.descripcion_validada,
    notas_observaciones: informe.notas?.general ?? "",
    responsables: Object.fromEntries(
      ROLES_RESPONSABLE.map(({ rol }) => [rol, String(informe.responsables?.[rol] ?? "")]),
    ) as InformeValues["responsables"],
  };
}

/* Referencia estable: una lista nueva en cada render rehace el contexto del resaltado. */
const SIN_SINCRONIZACIONES: SincronizacionDeTerreno[] = [];

/**
 * Edición del informe ya creado: la pantalla a la que se llega apenas termina
 * de crearlo, y desde el listado de informes en proceso.
 *
 * Fuera de alcance en este tramo, y declarado: "Compilar informe con IA"
 * (motor de IA diferido por el CLAUDE.md raíz), el contador de documentos
 * ADJUNTADOS —necesita la tabla `archivo`, que no existe— y las transiciones
 * de estado, que son del Módulo F.
 */
export function InformeEditar() {
  const { id, pestana: segmento } = useParams();
  const informeId = Number(id);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  /* A la edición se llega desde CUATRO pantallas y la url es la misma en todas,
     así que el origen viaja declarado (ver `lib/origenDeNavegacion.ts`). Sin
     él, quien entra desde la bandeja del revisor vuelve al listado del
     ejecutivo, donde nunca estuvo. */
  const origen = leerOrigen(params, ORIGEN_DE_LA_EDICION);
  const retorno = retornoA(origen, informeId);

  const ficha = useInforme(informeId);
  const catalogos = useCatalogosDelInforme();

  /* --- Qué pestaña se muestra, y por qué no se recalcula desde cero ---------

     EL REDIRECT ES CÓMO SE RESUELVE LA URL AL ENTRAR, NO UN INVARIANTE QUE SE
     REIMPONGA EN CADA RENDER. La diferencia no es teórica: `useInforme` no fija
     `staleTime` ni apaga `refetchOnWindowFocus`, así que rigen los defaults de
     TanStack Query y la ficha se revalida sola cada vez que la ventana recupera
     el foco.

     Reimponiéndolo, ese refetch devolviendo un estado donde la pestaña abierta
     ya no corresponde reemplaza `Contenido` por un `<Navigate>` — y eso
     DESMONTA el `useBlocker` que custodia los cambios sin guardar EN EL MISMO
     COMMIT en que navega, así que la guardia no llega a detener nada y el
     trabajo se pierde sin aviso. Sin que la persona haya tocado nada.

     La respuesta no es avisar mejor: es no sacarla. Una pestaña que ya se está
     mostrando sigue disponible aunque el estado se mueva abajo. Y la url no
     miente por eso — la barra de direcciones dice `suelos` y en pantalla hay
     suelos, que es lo único que el redirect existe para garantizar. Si el
     estado de verdad ya no la admite, el guardado falla con el 422 del
     backend: ruidoso y recuperable, al revés que perder lo escrito.

     SOLO SE PEGAN LAS DEL EXPEDIENTE. Son las que tienen formulario y trabajo
     sin guardar; las dos de mercado solo leen, así que sacarlas no le cuesta
     nada a nadie —y pegadas harían consultar comparables sobre un informe que
     el backend ya no considera aceptado.

     LO QUE SE PEGA LLEVA SU INFORME, y sin eso el arreglo se convierte en otro
     defecto: la misma ruta sirve a todos los informes, así que cambiar de `:id`
     NO vuelve a montar esta pantalla y el `useRef` viaja con el usuario. Con
     solo el slug adentro, saltar de `suelos` en un informe aceptado a `suelos`
     en otro que no admite técnicas dejaba la pestaña pegada por el anterior: se
     dibujaba una técnica —y con ella TODO el contenido técnico, porque
     `conTasacion` sale de esta misma lista— sobre un informe que el backend no
     acepta, y el redirect no se disparaba. Justo el estado que
     `pestanasDisponibles` existe para impedir. */
  const yaMostrada = useRef<{ informeId: number; pestana: PestanaDelInforme } | null>(null);
  const pedida = pestanaDesdeLaUrl(segmento);

  const admitidas = ficha.data ? pestanasDisponibles(ficha.data.estado) : [];
  const deEsteInforme =
    yaMostrada.current?.informeId === informeId ? yaMostrada.current.pestana : null;
  const pegada =
    deEsteInforme !== null &&
    esSeccionDelExpediente(deEsteInforme) &&
    !admitidas.includes(deEsteInforme)
      ? deEsteInforme
      : null;
  const disponibles = pegada !== null ? [...admitidas, pegada] : admitidas;

  /* `null` es "esta url no se puede mostrar", que es lo que dispara el
     redirect más abajo. Se registra DESPUÉS del render —en el efecto— para que
     `yaMostrada` diga lo que de verdad se pintó y no lo que se estaba por
     decidir. */
  const pestana = pedida !== null && disponibles.includes(pedida) ? pedida : null;

  useEffect(() => {
    yaMostrada.current = pestana === null ? null : { informeId, pestana };
  }, [informeId, pestana]);

  if (ficha.isPending || catalogos.isPending) {
    return (
      <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (ficha.isError || catalogos.isError || !ficha.data || !catalogos.data) {
    return (
      <div className="space-y-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        {/* SUBE a la raíz de la sección en vez de usar el retorno normal: el
            visado y la confirmación del envío cuelgan del MISMO informe que
            acaba de fallar, así que mandarían a otro error. La cola y la
            bandeja existen siempre. */}
        <SalidaDeEmergencia origen={origen} />
        <p className="text-destructive text-sm">
          No pudimos cargar este informe. Puede que ya no exista o que la conexión haya fallado.
        </p>
      </div>
    );
  }

  /* La pestaña sale de la URL y no de un `useState`: antes, recargar volvía
     siempre a "Antecedentes generales" con toda la carga de la pestaña
     perdida, no se podían abrir dos pestañas del mismo informe en dos
     ventanas, y no había enlace que compartir
     (`odd/tasks/pestanas-del-informe-en-la-url.md`).

     `pestanaDesdeLaUrl` da TRES respuestas y acá se resuelven en dos casos:
     sin segmento cae en la primera pestaña —`/informes/7` sigue siendo la url
     canónica y no rebota—, y cualquier otra cosa que no se pueda mostrar
     (inventada, o una técnica antes de la aceptación a la que nadie llegó a
     entrar) redirige. La barra de direcciones no puede decir una pestaña y
     mostrar otra. */
  if (pestana === null) {
    // `replace`: si no, "atrás" devuelve a la url que no sirve y el usuario
    // queda rebotando entre las dos.
    return (
      <Navigate to={rutaDePestana(informeId, PESTANA_POR_DEFECTO, location.search)} replace />
    );
  }

  return (
    <Contenido
      informe={ficha.data}
      catalogos={catalogos.data}
      pestana={pestana}
      /* Las pestañas que existen viajan RESUELTAS desde acá y `Contenido` no
         las recalcula: acá es donde se decidió el redirect, y con dos cuentas
         la barra podría dibujar una pestaña que la url rebota, o al revés. */
      disponibles={disponibles}
      onPestana={(destino) => navigate(rutaDePestana(informeId, destino, location.search))}
      onSalir={() => navigate(retorno.to)}
      retorno={retorno}
    />
  );
}

interface ContenidoProps {
  informe: Informe;
  catalogos: CatalogosDelInforme;
  pestana: PestanaDelInforme;
  /**
   * Las pestañas que existen para este informe, ya resueltas por quien decidió
   * el redirect. NO se recalculan acá: con dos cuentas, la barra podría dibujar
   * una pestaña que la url rebota, o al revés.
   */
  disponibles: PestanaDelInforme[];
  onPestana: (pestana: PestanaDelInforme) => void;
  onSalir: () => void;
  /** A dónde y con qué rótulo vuelve esta pantalla, según de dónde se entró. */
  retorno: { to: string; etiqueta: string };
}

/**
 * El cuerpo, separado del componente que resuelve la carga.
 *
 * No es cosmético: los hooks de mutación y los `useMemo` que dependen de la
 * ficha necesitan que `informe` ya exista. Dejándolos en el componente de
 * arriba habría que llamarlos antes de los `return` tempranos, con datos
 * todavía indefinidos.
 */
function Contenido({
  informe,
  catalogos,
  pestana,
  disponibles,
  onPestana,
  onSalir,
  retorno,
}: ContenidoProps) {
  const actualizar = useActualizarInforme(informe.id);
  const enviarATerreno = useEnviarATerreno(informe.id);
  const guardarDocumentos = useGuardarDocumentos(informe.id);
  const adjuntarArchivo = useAdjuntarArchivoDeAntecedente(informe.id);
  const quitarArchivo = useQuitarArchivoDeAntecedente(informe.id);
  const corregirFechaDelArchivo = useCorregirFechaDelArchivo(informe.id);
  const anotarElArchivo = useAnotarElArchivo(informe.id);
  const reprocesarArchivo = useReprocesarArchivoDeAntecedente(informe.id);
  const clasificarArchivo = useClasificarArchivoDeAntecedente(informe.id);
  const guardarDescripcion = useGuardarDescripcion(informe.id);
  const subirImagenDelRol = useSubirImagenDelRol(informe.id);
  const quitarImagenDelRol = useQuitarImagenDelRol(informe.id);
  const subirImagenDelExpediente = useSubirImagenDelExpediente(informe.id);
  const quitarImagenDelExpediente = useQuitarImagenDelExpediente(informe.id);
  const guardarSuelos = useGuardarSuelos(informe.id);
  const guardarAguas = useGuardarAguas(informe.id);
  const subirFotografia = useSubirFotografia(informe.id);
  const quitarFotografia = useQuitarFotografia(informe.id);

  /* Qué RECUADRO de la pestaña de descripción tiene una subida o un borrado en
     curso, para deshabilitar SOLO sus botones. Con una bandera global, adjuntar
     el límite de un rol congelaría los otros tres recuadros, que no tienen nada
     que ver. Mismo criterio que `documentoOcupado`. */
  const [imagenOcupada, setImagenOcupada] = useState<string | null>(null);

  /* Qué documento tiene una subida o un borrado en curso, para deshabilitar
     SOLO sus botones. Con una bandera global, adjuntar en una categoría
     congelaría las otras dos, que no tienen nada que ver. */
  const [documentoOcupado, setDocumentoOcupado] = useState<number | null>(null);

  /* Qué ARCHIVO tiene un "Reintentar" en vuelo, para deshabilitar SOLO su
     propio botón. Por `archivo.id` y no por `documento_tipo_id` como
     `documentoOcupado`: reintentar es una acción POR ARCHIVO —los demás
     archivos del mismo documento no tienen nada que ver con esta fila— y no
     debería tener que esperar a que se libere el documento entero. */
  const [archivoReprocesando, setArchivoReprocesando] = useState<number | null>(null);

  const valores = useMemo(() => valoresDesdeInforme(informe), [informe]);

  /* --- Aviso de cambios sin guardar (regla transversal, spec §13) ---------
     Solo una sección está montada por vez (las pestañas de Radix desmontan lo
     inactivo), así que alcanza con UNA bandera y UN par de referencias: la
     sección visible es la única que puede tener cambios pendientes. */
  const formRef = useRef<InformeFormularioHandle>(null);
  const documentosRef = useRef<PestanaDeDocumentosHandle>(null);
  const descripcionRef = useRef<PestanaDeDescripcionHandle>(null);
  const suelosRef = useRef<PestanaDeSuelosHandle>(null);
  const aguasRef = useRef<PestanaDeAguasHandle>(null);
  const maquinariaRef = useRef<PestanaDeMaquinariasHandle>(null);
  const instalacionesRef = useRef<PestanaDeInstalacionesHandle>(null);
  const construccionesRef = useRef<PestanaDeConstruccionesHandle>(null);
  const plantacionesRef = useRef<PestanaDePlantacionesHandle>(null);

  const [hayCambios, setHayCambios] = useState(false);
  /* El CLASIFICADOR es lo que le permite al aviso decir "cambias de pestaña"
     en vez de "sales de la pantalla" incluso cuando la salida la dispara el
     router y no un clic en la barra —el botón "atrás" entre dos pestañas—:
     `esCambioDePestanaDelMismoInforme` mira el DESTINO bloqueado y no cómo se
     llegó a él. */
  const guardia = useGuardiaDeCambios(hayCambios, (destino) =>
    esCambioDePestanaDelMismoInforme(informe.id, destino) ? "accion" : "navegacion",
  );

  /* --- Las tres pestañas con CUADRO -----------------------------------------
     Cada una arma sus seis mutaciones, sus avisos y el baile de subir las
     fotos después del alta. Estaba escrito a mano para maquinaria y ocupaba
     ~98 líneas de este archivo; copiado dos veces más lo habría llevado a
     cerca de 1.450.

     `manejarError` y `alGuardarConExito` se declaran más abajo y se pueden
     nombrar acá porque son declaraciones de función, que JavaScript eleva. */
  const maquinarias = useCuadroDelInforme({
    cuadro: "maquinarias",
    informe,
    laFila: "La máquina",
    laPestana: "Maquinarias y equipos",
    alGuardar: () => alGuardarConExito(() => maquinariaRef.current?.marcarGuardado()),
    alFallar: (error) => manejarError(error),
  });

  const instalaciones = useCuadroDelInforme({
    cuadro: "instalaciones",
    informe,
    laFila: "La instalación",
    laPestana: "Instalaciones y obras complementarias",
    alGuardar: () => alGuardarConExito(() => instalacionesRef.current?.marcarGuardado()),
    alFallar: (error) => manejarError(error),
  });

  const construcciones = useCuadroDelInforme({
    cuadro: "construcciones",
    informe,
    laFila: "La construcción",
    laPestana: "Construcciones",
    alGuardar: () => alGuardarConExito(() => construccionesRef.current?.marcarGuardado()),
    alFallar: (error) => manejarError(error),
  });

  const plantaciones = useCuadroDelInforme({
    cuadro: "plantaciones",
    informe,
    laFila: "La plantación",
    laPestana: "Descriptor de plantaciones",
    alGuardar: () => alGuardarConExito(() => plantacionesRef.current?.marcarGuardado()),
    alFallar: (error) => manejarError(error),
  });

  /* Estable: los hijos lo usan como dependencia de un efecto, y una identidad
     nueva por render lo dispararía en bucle. */
  const registrarCambios = useCallback((hay: boolean) => setHayCambios(hay), []);

  /* La guardia se lee desde callbacks de mutación que se crearon en un render
     anterior; por la referencia siempre ven la versión vigente. */
  const guardiaRef = useRef(guardia);
  useEffect(() => {
    guardiaRef.current = guardia;
  });

  /** El guardado en curso salió del aviso y hay que salir cuando termine. */
  const salirAlGuardar = useRef(false);

  function alGuardarConExito(marcarGuardado: () => void) {
    marcarGuardado();

    if (!salirAlGuardar.current) return;

    salirAlGuardar.current = false;
    guardiaRef.current.continuar();
  }

  /**
   * Qué guardado está en vuelo, POR PESTAÑA. Es lo que bloquea las tres salidas
   * del aviso de cambios sin guardar mientras el pedido viaja.
   *
   * ES UN `Record` SOBRE EL TIPO CERRADO Y NO UNA CADENA DE `||`, por el mismo
   * motivo que `guardarYSalir` es un `switch`: TypeScript EXIGE todas las
   * claves, así que una pestaña nueva sin su mutación no compila. La cadena
   * anterior se había olvidado de suelos y de aguas, y el efecto no era
   * cosmético —el spinner ausente era el síntoma—: con el guardado en vuelo los
   * tres botones seguían vivos, así que se podía apretar "Guardar y salir" otra
   * vez —dos PUT— o irse por "Salir sin guardar" con el primero viajando.
   *
   * Leer solo la pestaña ABIERTA es además más preciso que el `||`, que se
   * encendía con cualquier mutación. Da igual en la práctica —Radix desmonta lo
   * inactivo, así que solo una puede estar guardando— pero acá la relación
   * pestaña ↔ mutación queda escrita en vez de deducida.
   */
  const guardandoLaPestana: Record<PestanaDelInforme, boolean> = {
    general: actualizar.isPending,
    legal: guardarDocumentos.isPending,
    historico: guardarDocumentos.isPending,
    descripcion: guardarDescripcion.isPending,
    suelos: guardarSuelos.isPending,
    agua: guardarAguas.isPending,
    maquinaria: maquinarias.guardando,
    instalaciones: instalaciones.guardando,
    construcciones: construcciones.guardando,
    plantaciones: plantaciones.guardando,
    // Las de mercado solo leen: no tienen formulario ni nada que guardar.
    cbr: false,
    ofertas: false,
    // El registro de terreno también solo lee.
    terreno: false,
  };

  /**
   * Guarda la pestaña ABIERTA y recién entonces sale.
   *
   * ES UN `switch` SOBRE UN TIPO CERRADO Y NO UNA CADENA DE `if`, y esa
   * diferencia es la que evita el defecto que ya ocurrió: cuando entró la
   * pestaña "agua" nadie agregó su rama, el valor cayó al camino de "no hay
   * nada que guardar" y el botón que PROMETE guardar salía sin guardar. No
   * fallaba nada — ni error, ni aviso, ni nada en consola: el usuario apretaba
   * el botón correcto y su trabajo desaparecía.
   *
   * Cada pestaña va en el orden en que la barra las dibuja, así que la que
   * falte se ve leyendo. Y si aun así falta, el guardián del final no compila.
   */
  function guardarYSalir() {
    salirAlGuardar.current = true;

    switch (pestana) {
      case "general":
        formRef.current?.enviar(() => {
          // La validación frenó el envío: no hubo guardado, así que se cancela
          // la salida y se deja al usuario frente a los campos marcados.
          salirAlGuardar.current = false;
          guardia.cancelar();
          toast.error("Revisa los campos marcados antes de guardar.");
        });
        return;

      case "legal":
      case "historico":
        documentosRef.current?.guardar();
        return;

      case "descripcion":
        descripcionRef.current?.guardar();
        return;

      case "suelos":
        suelosRef.current?.guardar();
        return;

      case "agua":
        aguasRef.current?.guardar();
        return;

      case "maquinaria":
        maquinariaRef.current?.guardar();
        return;

      case "instalaciones":
        instalacionesRef.current?.guardar();
        return;

      case "construcciones":
        construccionesRef.current?.guardar();
        return;

      case "plantaciones":
        plantacionesRef.current?.guardar();
        return;

      /* Las pestañas de mercado no tienen formulario: no hay nada que guardar.
         Es inalcanzable desde la pantalla —no registran cambios, así que la
         guardia nunca se enciende sobre ellas— pero dejar la bandera en alto
         trabaría la salida esperando un guardado que no va a ocurrir.

         VAN COMO CASOS EXPLÍCITOS, y ahí está todo el asunto: mientras esto era
         el camino por defecto, "agua" cayó acá sin que nada lo dijera. Un
         "no hay nada que guardar" tiene que ser una DECISIÓN escrita sobre una
         pestaña concreta, nunca el lugar donde termina lo que nadie contempló. */
      case "cbr":
      case "ofertas":
      case "terreno":
        salirAlGuardar.current = false;
        guardia.continuar();
        return;
    }

    /* EL GUARDIÁN DE EXHAUSTIVIDAD.

       Acá abajo `pestana` ya es `never`: arriba están todos los casos de
       `PestanaDelInforme` y todos vuelven. El día que entre una pestaña nueva
       sin su rama, esta asignación NO COMPILA y `tsgo` se pone rojo señalando
       esta línea — que es exactamente lo que no pasó cuando entró "agua".

       Y si en ejecución llegara igual algo imposible —el valor viene de un
       `as PestanaDelInforme` sobre el string de Radix—, NO se sale: cancelar
       deja el trabajo en pantalla. Continuar sería repetir el defecto que este
       guardián existe para prevenir. */
    const _pestanaSinRamaDeGuardado: never = pestana;
    salirAlGuardar.current = false;
    guardia.cancelar();
    toast.error("No pudimos guardar esta pestaña. Tu trabajo sigue en pantalla.");
  }

  /* EL AVISO DE ARRIBA MIRA SOLO LAS TRES PESTAÑAS INICIALES, y no es un
     descuido: dice "complétalas para poder continuar con la cotización", y lo
     técnico se llena DESPUÉS de cotizar —recién existe cuando el cliente
     aceptó—. Incluirlo haría que reclamara algo que no bloquea nada, en todos
     los informes ya aceptados y para siempre.

     SALE DEL MISMO CÁLCULO QUE EL PUNTO DE COLOR DE LA BARRA, y eso es lo que
     cambió: antes había un mapa `pendientes` acá y la barra lo leía. Ahora los
     dos preguntan a `estadoDeLaPestana`. Con dos cuentas separadas el aviso
     podría reclamar sobre una pestaña que se ve verde —o callarse sobre una en
     rojo—, y cada mitad se leería como un defecto de la otra.

     PENDIENTE ES TODO LO QUE NO ESTÁ `listo`, no solo lo que está en rojo.

     LAS PESTAÑAS DE DOCUMENTOS NO CUENTAN AQUÍ, tampoco legales aunque su
     tarjeta vuelva a pintarse roja (03-10-2026): lo que reclama es que lleguen
     los archivos, y los papeles del cliente llegan DESPUÉS de cotizar —el
     portal se abre al aceptar—. Contarla haría que este aviso dijera "para
     poder continuar con la cotización" sobre algo que no la bloquea, en todo
     informe recién creado. Es la excepción declarada a "sale del mismo cálculo
     que la barra": el color de la tarjeta reclama los archivos, este aviso solo
     los datos que la cotización necesita. */
  const hayPendientes = PESTANAS.filter(
    ({ seccion }) => esPestanaInicial(seccion) && seccion !== "legal",
  ).some(({ seccion }) => estadoDeLaPestana(seccion, informe, catalogos).estado !== "listo");

  /* --- Qué pestañas existen --------------------------------------------------
     LLEGAN RESUELTAS POR PROP y acá no se recalculan: la lista sale de
     `pestanasDisponibles` (`lib/informes.ts`), donde está el POR QUÉ de cada
     grupo —las técnicas y las de mercado recién existen desde la aceptación,
     porque el backend responde 422 a cualquier estado anterior
     (`Informe::ESTADOS_CON_TASACION`); las de mercado además se apagan enteras
     si el módulo no está publicado en este despliegue, igual que su acceso del
     menú—, y la ajusta `InformeEditar`, que es donde se decidió el redirect.
     Con dos cuentas, la barra podría dibujar una pestaña que la url rebota, o
     al revés.

     Y la prop además trae la pestaña PEGADA: la que se está mostrando sigue en
     la lista aunque un refetch haya movido el estado abajo. `conTasacion` tiene
     que seguirla —es lo que decide si el `TabsContent` de las técnicas se
     dibuja—, porque si no el formulario se desmontaría igual y el trabajo se
     perdería, que es justo lo que quedarse existe para evitar.

     `conTasacion`/`conReferencias` se DERIVAN de la lista en vez de
     recalcularse con `tieneTasacionEnCurso`/`admiteReferenciasDeMercado`: son
     la forma de preguntar, sin repetir la condición, si el grupo técnico o el
     de mercado quedó incluido. */
  const pestanasVisibles = PESTANAS.filter(({ seccion }) => disponibles.includes(seccion));
  const conTasacion = pestanasVisibles.some(({ seccion }) => !esPestanaInicial(seccion));
  /* Se pregunta por LA LISTA de pestañas de mercado y no por `"cbr"` suelto: un
     slug escrito acá se desconecta en silencio el día que esa pestaña cambie de
     nombre o se retire, y el síntoma sería la barra dibujando dos pestañas cuyo
     contador nunca llega. */
  const conReferencias = PESTANAS_DE_MERCADO.some(({ pestana: valor }) =>
    disponibles.includes(valor),
  );

  /* UNA SOLA CONSULTA para los dos contadores de mercado, compartida por
     caché con la que hace cada pestaña al abrirse. Va con `null` cuando no
     corresponde, que es como `useReferenciasDelInforme` apaga la petición. */
  const enlazadas = useReferenciasDelInforme(conReferencias ? informe.id : null);

  const cuantasHay = (clase: ClaseDeEvidencia) =>
    (enlazadas.data?.referencias ?? []).filter((r) => r.clase === clase).length;

  /* EL REGISTRO DE TERRENO TIENE SU PROPIA CONSULTA y su propio permiso de
     existir: no cuelga del módulo de mercado. Se pregunta por la lista de
     pestañas disponibles y no por el estado, para que la barra y el redirect
     sigan diciendo lo mismo. */
  const conTerreno = disponibles.includes("terreno");
  const registroDeTerreno = useRegistroDeTerreno(conTerreno ? informe.id : null);
  /* El estado del registro lo leen DOS pestañas —Antecedentes generales y
     Construcciones— y los dos paneles de la descripción: se resuelve una vez. */
  const estadoDelRegistroDeTerreno = estadoDelTerreno({
    habilitado: conTerreno,
    error: registroDeTerreno.isError,
    registro: registroDeTerreno.data,
  });
  const asignadoEnTerreno = informe.terreno?.habilitado_para?.nombre ?? null;
  const verTerreno = conTerreno ? () => onPestana("terreno") : undefined;

  const barra = {
    informe,
    catalogos,
    pestanas: pestanasVisibles,
    terreno: conTerreno
      ? {
          etiqueta: "Terreno",
          cantidad: registroDeTerreno.data
            ? registroDeTerreno.data.totales.fotos +
              registroDeTerreno.data.totales.audios +
              registroDeTerreno.data.totales.textos
            : null,
        }
      : null,
    /* Vacío cuando no corresponden, y son DOS motivos distintos que acá dan lo
       mismo: el informe todavía no se aceptó, o el módulo está apagado en este
       despliegue. El conteo en `null` mientras viaja. */
    mercado: conReferencias
      ? PESTANAS_DE_MERCADO.map(({ pestana: valor, clase, etiqueta }) => ({
          pestana: valor,
          etiqueta,
          cantidad: enlazadas.data ? cuantasHay(clase) : null,
        }))
      : [],
  };

  function manejarError(error: unknown, errorDeCampo?: (c: string, m: string) => void) {
    /* Si el guardado venía del aviso, el fallo cancela la salida: quedarse con
       el cuadro abierto taparía el error que hay que leer. */
    if (salirAlGuardar.current) {
      salirAlGuardar.current = false;
      guardiaRef.current.cancelar();
    }

    const errores = parseValidationErrors(error);

    if (errores && errorDeCampo) {
      Object.entries(errores).forEach(([campo, mensaje]) => errorDeCampo(campo, mensaje));
      toast.error("Revisa los campos marcados.");
      return;
    }

    toast.error(getApiErrorMessage(error, "No pudimos guardar los cambios. Inténtalo de nuevo."));
  }

  return (
    /* UNA sola elección de sincronizaciones resaltadas para todo el editor: la
       que se hace en Maquinaria sigue puesta al pasar a Instalaciones. */
    <ProveedorDeResaltado sincronizaciones={registroDeTerreno.data?.sincronizaciones ?? SIN_SINCRONIZACIONES}>
    <ProveedorDeDescarteDeTerreno informeId={informe.id} habilitado={conTerreno}>
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <EnlaceVolver to={retorno.to}>{retorno.etiqueta}</EnlaceVolver>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-h2 text-ink">
            Informe: <span className="font-normal">{informe.propiedad_nombre ?? "—"}</span>
          </h2>
          {hayPendientes ? (
            <p className="text-danger-soft-foreground text-sm">
              Hay pestañas con campos sin definir. Complétalas para poder continuar con la
              cotización.
            </p>
          ) : null}
          {/* Metadata: mono y apagada, no compite con el contenido. */}
          <p className="text-caption text-ink-hint font-mono">
            Creado el {formatearMomento(informe.created_at)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* La puerta a la app de terreno. Solo en las etapas en que hay
              visita que hacer; antes el informe es un presupuesto. */}
          {admiteTerreno(informe.estado) && conTerreno ? (
            <BotonDeNotasGenerales
              informeId={informe.id}
              onVerEnRegistro={() => {
                onPestana("terreno");
                irALasNotasGenerales();
              }}
            />
          ) : null}
          {admiteTerreno(informe.estado) ? (
            <BotonGuiaDeTerreno informeId={informe.id} terreno={informe.terreno} />
          ) : null}
          {/* La puerta a la generación con IA; se dibuja sola desde la aceptación. */}
          <BotonDeGeneracion informeId={informe.id} estado={informe.estado} />
          <Badge variant={VARIANTE_ESTADO_INFORME[informe.estado]}>{ETIQUETA_ESTADO[informe.estado]}</Badge>
        </div>
      </div>

      {/* El siguiente paso del proceso, arriba de las pestañas: cotizar es lo
          que sigue después de completar el expediente, no una acción más del
          formulario. Se dibuja solo si el estado lo admite. */}
      <BannerDeCotizacion informe={informe} />

      <Tabs
        variant="tarjetas"
        value={pestana}
        /* Cambiar de pestaña ES una navegación: la pestaña viaja en la url, así
           que `onPestana` llama a `navigate()` y el router SÍ ve esta salida.
           Ya no hace falta interceptarla a mano —el `useBlocker` de la guardia
           la detiene solo, con el clasificador de más arriba diciendo que es
           "accion" y no "navegacion". */
        onValueChange={(v) => onPestana(v as PestanaDelInforme)}
      >
        {/* LA BARRA YA NO SCROLLEA EN HORIZONTAL: se reparte en dos bandas y
            entran las doce a la vez. Lo que había era una sola fila dentro de un
            `overflow-x-auto`, y en los equipos de Consultek —viewport de ~800 px,
            cuerpo de ~510— la mitad de las pestañas quedaba fuera de pantalla sin
            que nada indicara que estaban ahí. */}
        <BarraDePestanas {...barra} />

        {/* El título de la pestaña activa. Recibe EXACTAMENTE lo mismo que la
            barra, para que el estado del título y el de la tarjeta salgan de la
            misma cuenta. */}
        <EncabezadoDePestana {...barra} activa={pestana} />

        {/* Qué llegó de terreno en las sincronizaciones elegidas. Solo en las
            pestañas técnicas y solo si el informe tiene sincronizaciones. */}
        <BarraDeSincronizaciones
          informeId={informe.id}
          pestana={pestana}
          registro={registroDeTerreno.data}
        />

        {/* Habla de definir la obligatoriedad de los documentos, así que solo
            corresponde sobre las tres del expediente: encima de una lista de
            comparables no describiría nada de lo que está en pantalla. */}
        {esPestanaInicial(pestana) && (
          <p className="text-ink-muted mt-3 text-sm">
            Todos los documentos de Antecedentes legales e Históricos son obligatorios por defecto:
            marca como opcionales o "No aplica" los que correspondan. El resto de las pestañas se
            habilita una vez que el cliente aprueba la cotización.
          </p>
        )}

        <TabsContent value="general" className="mt-5 space-y-5">
          {/* La facturación va ARRIBA y de solo lectura: describe al MISMO
              cliente que el formulario de abajo nombra y contacta, así que
              quien completa esa ficha tiene al lado con qué datos se le va a
              facturar. Se dibuja siempre —también vacía—: "todavía no los
              mandó" es un estado del proceso que hay que poder ver, no una
              tarjeta que sobra. */}
          <InformeFormulario
            ref={formRef}
            encabezado={
              <FacturacionDelCliente
                facturacion={informe.facturacion}
                visitadoEn={informe.portal_visitado_en}
              />
            }
            // Lo que mandó terreno para esta pestaña. Es la MISMA consulta que
            // alimenta el contador de la barra, así que abrir la pestaña no pide
            // nada nuevo; si falla o todavía no existe, el formulario no se entera.
            terreno={estadoDelRegistroDeTerreno}
            // El salto es la MISMA navegación que la barra (`onPestana`), así que
            // la guardia de cambios sin guardar lo trata igual.
            onVerTerreno={verTerreno}
            catalogos={catalogos}
            modo="editar"
            // Acá el informe SÍ existe, así que las corridas de IA quedan
            // atadas a él. En la creación no se pasa: todavía no hay ninguno.
            informeId={informe.id}
            informe={informe}
            etiquetaEnvio="Guardar cambios"
            valoresIniciales={valores}
            enviando={actualizar.isPending}
            onCambios={registrarCambios}
            onCancelar={onSalir}
            onEnviar={(valoresDelForm, input, { errorDeCampo, enviarGuiaAlGuardar }) =>
              actualizar.mutate(
                {
                  ...input,
                  descripcion_ubicacion_validada: valoresDelForm.descripcion_ubicacion_validada,
                  descripcion_validada: valoresDelForm.descripcion_validada,
                  notas_observaciones: valoresDelForm.notas_observaciones || null,
                },
                {
                  // La nueva base del formulario sale de la RESPUESTA, no de lo
                  // que quedó en pantalla: el servidor congela el nombre de la
                  // comuna desde `comuna_codigo` y asigna el `id` de cada rol
                  // recién creado, ninguno de los dos presente en el payload
                  // que se mandó.
                  onSuccess: (guardado) => {
                    toast.success("Cambios guardados.");
                    alGuardarConExito(() =>
                      formRef.current?.marcarGuardado(valoresDesdeInforme(guardado)),
                    );

                    // Se cambió el encargado de un informe que estaba en la app
                    // y se eligió enviarle la guía al nuevo. Va DESPUÉS de
                    // guardar: el servidor se la manda al encargado vigente, y
                    // antes del guardado ese sigue siendo el anterior.
                    if (enviarGuiaAlGuardar) {
                      enviarATerreno.mutate(undefined, {
                        onSuccess: (respuesta) =>
                          respuesta.correo_enviado
                            ? toast.success("Guía enviada al nuevo encargado de terreno.")
                            : toast.warning(
                                "El informe ya está en la app del nuevo encargado, pero el correo no salió. Reenvía la guía en unos minutos.",
                              ),
                        onError: (error) =>
                          toast.error(
                            getApiErrorMessage(
                              error,
                              "Los cambios se guardaron, pero no pudimos enviar la guía. Envíala desde \"Guía de terreno\".",
                            ),
                          ),
                      });
                    }
                  },
                  onError: (error) => manejarError(error, errorDeCampo),
                },
              )
            }
          />
        </TabsContent>

        {(["legal", "historico"] as const).map((categoria) => (
          <TabsContent key={categoria} value={categoria} className="mt-5">
            <PestanaDeDocumentos
              // `key` con el id del informe: si no, al cambiar de informe React
              // reutilizaría el componente y conservaría el estado local de la
              // pestaña anterior (la definición y las notas del OTRO informe).
              key={`${informe.id}-${categoria}`}
              // Una sola referencia para las dos pestañas: Radix desmonta la
              // inactiva, así que nunca hay dos montadas peleándose el ref.
              ref={documentosRef}
              onCambios={registrarCambios}
              categoria={categoria}
              expedienteHabilitado={conTasacion}
              documentos={catalogos.documentos[categoria] ?? []}
              catalogos={catalogos.documentos}
              definicionInicial={definicionDesdeInforme(
                (informe.documentos ?? []).filter((d) =>
                  (catalogos.documentos[categoria] ?? []).some((c) => c.id === d.documento_tipo_id),
                ),
              )}
              notasIniciales={informe.notas?.[categoria] ?? ""}
              guardando={guardarDocumentos.isPending}
              /* LAS DOS PESTAÑAS ADJUNTAN, también la de LEGALES. Ahí el
                 prototipo no dibujaba el botón y durante un tiempo la pestaña
                 fue de solo lectura: esos documentos entraban por el portal del
                 cliente (`/portal/{token}`, adonde llevan los dos botones del
                 correo de aceptación) y abrir acá una segunda puerta parecía
                 mezclar dos flujos. En la práctica el cliente manda sus papeles
                 por fuera del portal tanto como por él —correo, mensajería, en
                 mano— y sin esta puerta el ejecutivo no tenía dónde dejarlos: se
                 quedaban afuera del expediente.

                 El portal no pierde nada. Cada archivo sigue diciendo de dónde
                 vino, y lo que mandó el cliente no se puede quitar desde acá
                 (ver `ArchivosDelDocumento`, y el 403 que lo custodia en el
                 servidor). */
              adjuntos={{
                archivos: informe.archivos ?? [],
                urlDeDescarga: (archivoId) =>
                  urlDeArchivoDeAntecedente(informe.id, archivoId),
                ocupadoEn: documentoOcupado,
                onAdjuntar: async (documentoTipoId, archivos) => {
                  /* El documento queda ocupado por TODO el lote, no por cada
                     archivo: entre una subida y la siguiente no hay un hueco
                     donde el botón se rehabilite. */
                  setDocumentoOcupado(documentoTipoId);
                  try {
                    const { subidos, fallos } = await subirEnSecuencia(
                      archivos,
                      (archivo) => adjuntarArchivo.mutateAsync({ documentoTipoId, archivo }),
                      (error) =>
                        parseValidationErrors(error)?.archivo ??
                        getApiErrorMessage(error, "No pudimos adjuntar el archivo."),
                    );

                    if (subidos.length > 0) {
                      toast.success(
                        subidos.length === 1
                          ? "Archivo adjuntado."
                          : `${subidos.length} archivos adjuntados.`,
                      );
                    }
                    if (fallos.length > 0) {
                      toast.error(
                        fallos.length === 1
                          ? "No se pudo adjuntar un archivo."
                          : `No se pudieron adjuntar ${fallos.length} archivos.`,
                      );
                    }

                    return fallos;
                  } finally {
                    setDocumentoOcupado(null);
                  }
                },
                onQuitar: (archivoId) => {
                  const documentoTipoId = (informe.archivos ?? []).find(
                    (a) => a.id === archivoId,
                  )?.documento_tipo_id;
                  setDocumentoOcupado(documentoTipoId ?? null);
                  quitarArchivo.mutate(archivoId, {
                    onSuccess: () => toast.success("Archivo quitado."),
                    onError: (error) => manejarError(error),
                    onSettled: () => setDocumentoOcupado(null),
                  });
                },
                onCorregirFecha: (archivoId, fecha) => {
                  corregirFechaDelArchivo.mutate(
                    { archivoId, fecha },
                    {
                      onSuccess: () => toast.success("Fecha corregida."),
                      onError: (error) => manejarError(error),
                    },
                  );
                },
                onAnotar: (archivoId, nota) => {
                  anotarElArchivo.mutate(
                    { archivoId, nota },
                    {
                      onSuccess: () => toast.success("Nota guardada."),
                      onError: (error) => manejarError(error),
                    },
                  );
                },
                onReprocesar: (archivoId) => {
                  setArchivoReprocesando(archivoId);
                  reprocesarArchivo.mutate(archivoId, {
                    onSuccess: () => toast.success("El archivo se está reprocesando."),
                    onError: (error) => manejarError(error),
                    onSettled: () => setArchivoReprocesando(null),
                  });
                },
                /* Clasificar, reclasificar o devolver a "sin clasificar". Resuelve
                   `false` ante un rechazo del servidor (p. ej. el mismo archivo
                   ya está bajo ese tipo): el motivo sale como aviso y el selector
                   queda abierto para elegir otro. */
                onClasificar: async (archivoId, documentoTipoId) => {
                  try {
                    await clasificarArchivo.mutateAsync({ archivoId, documentoTipoId });
                    toast.success(
                      documentoTipoId === null
                        ? "Archivo devuelto a sin clasificar."
                        : "Archivo clasificado.",
                    );
                    return true;
                  } catch (error) {
                    manejarError(error);
                    return false;
                  }
                },
                archivoReprocesando,
              }}
              onGuardar={(definicion, notas) =>
                guardarDocumentos.mutate(
                  { categoria, documentos: definicion, notas: notas || null },
                  {
                    onSuccess: () => {
                      toast.success("Antecedentes guardados.");
                      alGuardarConExito(() => documentosRef.current?.marcarGuardado());
                    },
                    onError: (error) => manejarError(error),
                  },
                )
              }
            />
          </TabsContent>
        ))}

        {conTasacion && (
          <TabsContent value="descripcion" className="mt-5">
            <PestanaDeDescripcion
              /* `key` con el id del informe, igual que la de documentos: sin
                 él, al cambiar de informe React reutilizaría el componente y
                 conservaría el estado local del OTRO — sus deslindes y sus
                 notas. */
              key={`${informe.id}-descripcion`}
              ref={descripcionRef}
              informe={informe}
              guardando={guardarDescripcion.isPending}
              ocupadaEn={imagenOcupada}
              onCambios={registrarCambios}
              onImagenDelRol={(rolId, archivo) => {
                setImagenOcupada(`rol-${rolId}`);
                subirImagenDelRol.mutate(
                  { rolId, archivo },
                  {
                    onSuccess: () => toast.success("Imagen guardada."),
                    onError: (error) => manejarError(error),
                    onSettled: () => setImagenOcupada(null),
                  },
                );
              }}
              onQuitarImagenDelRol={(rolId) => {
                setImagenOcupada(`rol-${rolId}`);
                quitarImagenDelRol.mutate(rolId, {
                  onSuccess: () => toast.success("Imagen quitada."),
                  onError: (error) => manejarError(error),
                  onSettled: () => setImagenOcupada(null),
                });
              }}
              onImagenDelExpediente={(tipo, archivo) => {
                setImagenOcupada(tipo);
                subirImagenDelExpediente.mutate(
                  { tipo, archivo },
                  {
                    onSuccess: () => toast.success("Imagen guardada."),
                    onError: (error) => manejarError(error),
                    onSettled: () => setImagenOcupada(null),
                  },
                );
              }}
              onQuitarImagenDelExpediente={(tipo: TipoDeImagenDelInforme) => {
                setImagenOcupada(tipo);
                quitarImagenDelExpediente.mutate(tipo, {
                  onSuccess: () => toast.success("Imagen quitada."),
                  onError: (error) => manejarError(error),
                  onSettled: () => setImagenOcupada(null),
                });
              }}
              onGuardar={(roles, notas, avaluoFiscalPeriodo) =>
                guardarDescripcion.mutate(
                  { roles, notas_observaciones: notas || null, avaluo_fiscal_periodo: avaluoFiscalPeriodo },
                  {
                    onSuccess: () => {
                      toast.success("Descripción guardada.");
                      alGuardarConExito(() => descripcionRef.current?.marcarGuardado());
                    },
                    onError: (error) => manejarError(error),
                  },
                )
              }
            />
          </TabsContent>
        )}

        {conTasacion && (
          <TabsContent value="suelos" className="mt-5">
            <PestanaDeSuelos
              /* `key` con el id del informe, igual que las otras: sin él, al
                 cambiar de informe React reutilizaría el componente y
                 conservaría el estado local del OTRO — su cuadro y sus
                 notas. */
              key={`${informe.id}-suelos`}
              ref={suelosRef}
              informe={informe}
              // La MISMA consulta del registro de terreno que Antecedentes y
              // Construcciones: abrir la pestaña no pide nada nuevo. Con el
              // módulo apagado la pestaña se dibuja sin ninguna pieza de terreno.
              terreno={estadoDelRegistroDeTerreno}
              asignado={asignadoEnTerreno}
              onVerTerreno={verTerreno}
              guardando={guardarSuelos.isPending}
              ocupadaEn={imagenOcupada}
              onCambios={registrarCambios}
              onImagenDelExpediente={(tipo, archivo) => {
                setImagenOcupada(tipo);
                subirImagenDelExpediente.mutate(
                  { tipo, archivo },
                  {
                    onSuccess: () => toast.success("Imagen guardada."),
                    onError: (error) => manejarError(error),
                    onSettled: () => setImagenOcupada(null),
                  },
                );
              }}
              onQuitarImagenDelExpediente={(tipo: TipoDeImagenDelInforme) => {
                setImagenOcupada(tipo);
                quitarImagenDelExpediente.mutate(tipo, {
                  onSuccess: () => toast.success("Imagen quitada."),
                  onError: (error) => manejarError(error),
                  onSettled: () => setImagenOcupada(null),
                });
              }}
              onFotografia={(tipo, archivo) => {
                setImagenOcupada(tipo);
                // `mutateAsync` y no `mutate`: la galería sube varias fotos
                // seguidas y espera a cada una antes de mandar la siguiente.
                return subirFotografia
                  .mutateAsync({ tipo, archivo })
                  .then(() => toast.success("Fotografía guardada."))
                  .catch((error) => manejarError(error))
                  .finally(() => setImagenOcupada(null));
              }}
              onQuitarFotografia={(fotografiaId) => {
                /* La bandera es del BLOQUE y no de la foto: quitar una
                   deshabilita el bloque entero mientras dura, que es lo
                   correcto — la lista se está por reordenar. */
                setImagenOcupada("terreno_topografia");
                quitarFotografia.mutate(fotografiaId, {
                  onSuccess: () => toast.success("Fotografía quitada."),
                  onError: (error) => manejarError(error),
                  onSettled: () => setImagenOcupada(null),
                });
              }}
              onGuardar={(input) =>
                guardarSuelos.mutate(input, {
                  onSuccess: () => {
                    toast.success("Descripción de suelos guardada.");
                    alGuardarConExito(() => suelosRef.current?.marcarGuardado());
                  },
                  onError: (error) => manejarError(error),
                })
              }
            />
          </TabsContent>
        )}

        {conTasacion && (
          <TabsContent value="agua" className="mt-5">
            <PestanaDeAguas
              /* `key` con el id del informe, igual que las otras: sin él, al
                 cambiar de informe React reutilizaría el componente y
                 conservaría el estado local del OTRO — sus dos cuadros y sus
                 notas. */
              key={`${informe.id}-agua`}
              ref={aguasRef}
              informe={informe}
              // La MISMA consulta del registro de terreno que Suelos y
              // Construcciones: lo que el encargado creó en la App llega como
              // filas TERRENO, y lo registrado en cada fila se ve bajo ella.
              terreno={estadoDelRegistroDeTerreno}
              asignado={asignadoEnTerreno}
              onVerTerreno={verTerreno}
              guardando={guardarAguas.isPending}
              onCambios={registrarCambios}
              onGuardar={(input) =>
                guardarAguas.mutate(input, {
                  onSuccess: (guardado) => {
                    toast.success("Descriptor de aguas guardado.");
                    // El informe devuelto trae los ids de las filas nuevas: sin
                    // ellos, un segundo guardado las crearía otra vez.
                    alGuardarConExito(() => aguasRef.current?.marcarGuardado(guardado));
                  },
                  onError: (error) => manejarError(error),
                })
              }
            />
          </TabsContent>
        )}

        {conTasacion && (
          <TabsContent value="maquinaria" className="mt-5">
            <PestanaDeMaquinarias
              /* `key` con el id del informe, igual que las otras: sin él, al
                 cambiar de informe React reutilizaría el componente y
                 conservaría el estado local del OTRO — su formulario a medio
                 llenar y sus fotos pendientes. */
              key={`${informe.id}-maquinaria`}
              ref={maquinariaRef}
              informe={informe}
              // La MISMA consulta del registro de terreno que Aguas: lo que el
              // encargado creó en la App llega como filas TERRENO y lo
              // registrado en cada máquina se ve bajo ella.
              terreno={estadoDelRegistroDeTerreno}
              asignado={asignadoEnTerreno}
              onCambios={registrarCambios}
              {...maquinarias}
            />
          </TabsContent>
        )}

        {conTasacion && (
          <TabsContent value="instalaciones" className="mt-5">
            <PestanaDeInstalaciones
              key={`${informe.id}-instalaciones`}
              ref={instalacionesRef}
              informe={informe}
              // La MISMA consulta del registro de terreno que Aguas y Maquinaria.
              terreno={estadoDelRegistroDeTerreno}
              asignado={asignadoEnTerreno}
              onCambios={registrarCambios}
              {...instalaciones}
            />
          </TabsContent>
        )}

        {conTasacion && (
          <TabsContent value="construcciones" className="mt-5">
            <PestanaDeConstrucciones
              key={`${informe.id}-construcciones`}
              ref={construccionesRef}
              informe={informe}
              // La MISMA consulta del registro de terreno que Antecedentes
              // generales: abrir la pestaña no pide nada nuevo. Con el módulo
              // apagado la pestaña se dibuja sin ninguna pieza de terreno.
              terreno={estadoDelRegistroDeTerreno}
              asignado={asignadoEnTerreno}
              onVerTerreno={verTerreno}
              onCambios={registrarCambios}
              {...construcciones}
            />
          </TabsContent>
        )}

        {conTasacion && (
          <TabsContent value="plantaciones" className="mt-5">
            <PestanaDePlantaciones
              key={`${informe.id}-plantaciones`}
              ref={plantacionesRef}
              informe={informe}
              // La MISMA consulta del registro de terreno que Aguas y
              // Maquinarias: las plantaciones que el encargado creó en la App
              // llegan como filas TERRENO y lo registrado en cada una se ve
              // bajo ella.
              terreno={estadoDelRegistroDeTerreno}
              asignado={asignadoEnTerreno}
              onCambios={registrarCambios}
              {...plantaciones}
            />
          </TabsContent>
        )}

        {conReferencias &&
          PESTANAS_DE_MERCADO.map(({ pestana: valor, clase }) => (
            <TabsContent key={valor} value={valor} className="mt-5">
              <PestanaDeReferencias
                informeId={informe.id}
                clase={clase}
                /* CONVERTIDA ACÁ y no en la pestaña: la unidad es del INFORME
                   —un total no puede sumar 8.000 m2 con 23 ha— y esta es la
                   pantalla que la conoce. Sin convertir, un predio en m2 daría
                   un valor total diez mil veces más grande. */
                superficieDelPredioHa={superficieEnHectareas(
                  informe.superficie_ha,
                  informe.unidad_superficie,
                )}
              />
            </TabsContent>
          ))}

        {conTerreno && (
          <TabsContent value="terreno" className="mt-5">
            {/* Solo lee: el salto a otra pestaña es la MISMA navegación que la
                barra (`onPestana`), así que la guardia de cambios sin guardar
                la trata igual. */}
            <PestanaDeTerreno
              informeId={informe.id}
              onAbrirPestana={onPestana}
            />
          </TabsContent>
        )}
      </Tabs>

      <DialogoCambiosSinGuardar
        abierto={guardia.bloqueado}
        descripcion={
          guardia.motivo === "accion"
            ? "Esta pestaña tiene cambios que no se han guardado. Si cambias de pestaña ahora se perderán y el informe quedará como estaba en el último guardado."
            : "Este informe tiene cambios que no se han guardado. Si sales ahora se perderán y el informe quedará como estaba en el último guardado."
        }
        guardando={guardandoLaPestana[pestana]}
        onGuardarYSalir={guardarYSalir}
        rotuloGuardar={guardia.motivo === "accion" ? "Guardar y continuar" : "Guardar y salir"}
        onSalirSinGuardar={guardia.continuar}
        onCancelar={guardia.cancelar}
      />
    </div>
    </ProveedorDeDescarteDeTerreno>
    </ProveedorDeResaltado>
  );
}

/**
 * La salida cuando el informe no cargó.
 *
 * Va a la RAÍZ de la sección de la que se vino —la cola o la bandeja— y no al
 * retorno normal: los orígenes `visado` y `cotizacion` cuelgan del mismo
 * informe que acaba de fallar, así que llevarían a otro error. Conserva la
 * sección igual, para no soltar al revisor en el listado del ejecutivo.
 */
function SalidaDeEmergencia({ origen }: { origen: Origen }) {
  const salida = retornoA(raizDelOrigen(origen), 0);

  return <EnlaceVolver to={salida.to}>{salida.etiqueta}</EnlaceVolver>;
}
